import React, { type ReactNode, type DetailedHTMLProps, type HTMLAttributes, type FormEvent, forwardRef, useRef, FocusEventHandler } from "react";
import { twMerge, twJoin } from "tailwind-merge";

import { SupText } from "../../../components/UI/Text";
import { useControlledState } from "../../../hooks/useControlledState";
import { useMergedRef } from "../../../hooks/useMergedRef";
import { useAutoFocus } from "../../../hooks/useAutoFocus";
import SupXCircle from "../../../icons/XCircle";
import type { CommonColors } from "../../../types/colors";

export interface InputProps {
  id?: string;
  name?: string;
  value?: string;
  defaultValue?: string;
  color?: CommonColors;
  disabled?: boolean;
  placeholder?: string;
  type?: "text" | "hidden" | "number" | "password" | "tel" | "url" | "email";
  autoFocus?: boolean;
  className?: string;
  fluid?: boolean;
  suffixIcon?: ReactNode;
  prefixIcon?: ReactNode;
  clearable?: boolean;
  readOnly?: boolean;
  inputProps?: Omit<
    DetailedHTMLProps<HTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    "id" | "name" | "value" | "defaultValue" | "disabled" | "placeholder" | "type" | "autoFocus" | "ref" | "readonly"
  > & { pattern?: string };
  formatter?: (value: string, prevValue?: string) => string | undefined;
  onChange?: (value?: string) => void;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
}

export const borderColors = {
  primary: "border-0.5 border-neutral-500 enabled:hover:border-neutral-700 enabled:focus:border-neutral-500 enabled:active:border-neutral-800",
  success: "border-0.5 border-success-900",
  info: "border-0.5 border-info-800",
  error: "border-0.5 border-error-925",
  warning: "border-0.5 border-warning-900",
} as const;

const defaultFormatter = (value: string) => value;

export const SupInput = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    id,
    name,
    disabled,
    placeholder,
    type = "text",
    inputProps,
    value,
    defaultValue,
    className,
    autoFocus = false,
    color = "primary",
    fluid = false,
    clearable = false,
    suffixIcon,
    prefixIcon,
    formatter = defaultFormatter,
    readOnly = false,
    onChange,
    onBlur,
    onFocus,
  },
  forwardedRef
) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useControlledState<string | undefined>({ value, defaultValue, onStateChange: onChange });
  // @ts-ignore
  const elRef = useMergedRef(forwardedRef, inputRef);

  useAutoFocus(elRef.current, { enabled: autoFocus });

  const displayClearableButton = clearable && inputValue != null && inputValue !== "" && !disabled;

  const handleStateChange = (e: FormEvent<HTMLInputElement>) => {
    setInputValue(formatter(e.currentTarget.value, inputValue));
  };

  const resetState = () => {
    setInputValue("");
  };

  return (
    <div className={twMerge("sup-input text-neutral-900 inline-flex items-center relative", fluid && "w-full", className)}>
      {Boolean(prefixIcon) && <span className={twJoin("absolute left-4", disabled && !readOnly && "opacity-16")}>{prefixIcon}</span>}
      <SupText
        {...inputProps}
        ref={elRef}
        as="input"
        variant="subtitle1"
        value={inputValue}
        id={id}
        name={name}
        disabled={disabled}
        placeholder={placeholder}
        type={type}
        onChange={handleStateChange}
        onBlur={onBlur}
        onFocus={onFocus}
        className={twMerge(
          "flex-1 w-full h-[3.25rem] outline-0 transition-colors duration-200 placeholder:text-mono placeholder:text-neutral-600 placeholder:text-xs placeholder:leading-4 border-0.5 bg-neutral-50 enabled:hover:bg-neutral-75 enabled:focus:bg-neutral-50 enabled:active:bg-neutral-75",
          prefixIcon ? "pl-11" : "pl-4",
          suffixIcon ? "pr-11" : "pr-4",
          !disabled ? borderColors[color] : "disabled:border-neutral-100",
          disabled && !readOnly && "disabled:bg-neutral-50 disabled:text-neutral-300",
          readOnly && "cursor-not-allowed disabled:opacity-90",
          inputProps?.className
        )}
      />
      {Boolean(suffixIcon) && !displayClearableButton && <span className={twJoin("absolute right-4", disabled && !readOnly && "opacity-16")}>{suffixIcon}</span>}
      {displayClearableButton && (
        <button className="absolute right-4" onClick={resetState}>
          <SupXCircle />
        </button>
      )}
    </div>
  );
});
